import { guestClient } from '@/api/guestClient'
import { apiClient } from '@/api'

const BASE = '/submittals'

export const getSubmittalMeta = async (submittalId, consultantId) => {
  const res = await guestClient.get(`${BASE}/${submittalId}/meta`, {
    params: { consultantId },
  })
  return res.data
}

export const getSubmittalDetails = async submittalId => {
  const res = await apiClient.get(`${BASE}/${submittalId}/details`)
  return res.data
}

export const getSubmittalSchema = async dataElementId => {
  const res = await apiClient.get(`/data-elements/${dataElementId}`)
  return res.data
}

export const getAllDataElements = async () => {
  const res = await apiClient.get('/data-elements')
  return res.data
}

export const approveSubmittal = async submittalId => {
  const res = await apiClient.post(`${BASE}/${submittalId}/approve`)
  return res.data
}

export const rejectSubmittal = async (submittalId, reason) => {
  const res = await apiClient.post(`${BASE}/${submittalId}/reject`, { reason })
  return res.data
}

/**
 * A submittal's activity log, newest first, always as an array.
 *
 * The endpoint returns a bare list today, but every other listing on this
 * service wraps its rows in a `{ data: [...] }` envelope. Normalised here, as
 * pitstop does, so an envelope arriving later renders a timeline rather than
 * silently nothing — the callers map over this directly.
 */
export const getActivityLogs = async submittalId => {
  const res = await apiClient.get(`${BASE}/${submittalId}/activity-logs`)
  return Array.isArray(res.data) ? res.data : res.data?.data || []
}

/**
 * Serialises listing parameters.
 *
 * Every key given is sent — no allowlist. A configured listing derives its
 * filter parameters from `listingConfig.filters`, so the set is not known at
 * build time: an allowlist here would silently drop a filter authored in
 * DynamoDB, which reads as a dropdown that selects and changes nothing. Arrays
 * repeat the key, which is how the handler reads a multi-value filter
 * (`q["status"]`).
 */
const toListingQuery = (params = {}) => {
  const queryParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    if (Array.isArray(value)) {
      value.forEach(entry => {
        if (entry === undefined || entry === null || entry === '') return
        queryParams.append(key, entry)
      })
      return
    }
    queryParams.append(key, value)
  })

  return queryParams.toString()
}

const toPagination = (pagination = {}) => ({
  currentPage: pagination.page,
  totalPages: pagination.total_pages,
  totalItems: pagination.total,
  hasNext: pagination.page < pagination.total_pages,
  hasPrev: pagination.page > 1,
})

/**
 * Rows as the API returns them, unreshaped.
 *
 * A configured listing reads `row[column.key]` for whatever keys its
 * `listingConfig` names, so nothing may be dropped on the way through: the six
 * precast columns (`submittal_id`, `submission_date`, `project_contract_number`,
 * `project_contract_name`, `main_contractor_company_name`, `status`) are all
 * root fields on the response, and a column added to the config later must not
 * need a change here to appear.
 */
export const getConsultantSubmittals = async (params = {}) => {
  try {
    const qs = toListingQuery(params)
    const res = await apiClient.get(`consultant${BASE}${qs ? `?${qs}` : ''}`)
    const response = res.data

    return {
      data: response?.data || [],
      pagination: toPagination(response?.pagination),
      success: true,
    }
  } catch (error) {
    console.error('Error fetching submittal listing:', error)
    throw new Error(error.response?.data?.detail || 'Failed to fetch submittals')
  }
}

/**
 * The V1 (Environmental Monthly Submissions) listing's rows, flattened to the
 * fixed set of fields its hardcoded columns read. `project_contract_name` is
 * lifted out of the payload, falling back to the promoted root column.
 *
 * Sends no `payload_fields_to_extract`, as it always has: the API then returns
 * the payload whole, and narrowing it is a behaviour change this listing has no
 * need for.
 */
export const getSubmittalListing = async (params = {}) => {
  const response = await getConsultantSubmittals(params)

  return {
    ...response,
    data: response.data.map(item => ({
      id: item.submittal_id,
      data_element_id: item.data_element_id,
      submission_month: item.submission_month,
      modified: item.modified,
      project_contract_name:
        item.payload?.project_details?.project_contract_name || item.project_contract_name || '',
      request_id: item.request_id,
      status: item.status,
    })),
  }
}

/**
 * Matches `filename="..."` in a Content-Disposition header, quotes optional.
 *
 * Only the plain parameter: the service sends an ASCII name, so the RFC 5987
 * `filename*` form is not attempted.
 */
const FILENAME_PATTERN = /filename="?([^";]+)"?/i

/**
 * A submittal's export, with the name the service gave it.
 *
 * The name is whatever the server put in Content-Disposition rather than
 * anything derived here, so the consultant saves the same file the submitter
 * does from pitstop, which reads the header the same way.
 *
 * Today that name is `{data_element_id}.csv` — the handler builds it from the id
 * alone, falling back to the submittal's UUID — so honouring it changes nothing
 * a user can see. It is read anyway because naming the download is the service's
 * job: when the handler learns to name the file after the contract and month,
 * both apps pick that up without a frontend change.
 *
 * `fileName` is null when the header is absent — or not exposed, which is a
 * per-deployment CORS decision — and that is the caller's cue to name the file
 * itself.
 */
export const downloadSubmittalCsv = async (submittalId, format = 'csv') => {
  try {
    const res = await apiClient.get(`submittals/download/${submittalId}/${format}`, {
      responseType: 'text',
    })
    const disposition = res.headers?.['content-disposition'] || ''
    return {
      data: res.data,
      fileName: FILENAME_PATTERN.exec(disposition)?.[1] || null,
      error: null,
    }
  } catch (error) {
    return {
      data: null,
      fileName: null,
      error: error.response?.data?.detail || 'Failed to download file',
    }
  }
}

/**
 * Values for the filter dropdowns.
 *
 * `dataElementId` scopes the lists to one data element. The configured listing
 * is per-data-element — one tab each — so without it a tab offers values drawn
 * from another tab's submittals, which select to an empty table.
 *
 * The response is passed through whole, because a configured filter names the
 * list it reads via `optionsFrom` and looks it up by that name. Which names
 * exist is not known at build time — a filter authored in DynamoDB can point at
 * a list this file has never heard of (`precast_types`) — so an allowlist here
 * would drop it, and `availableFilters` renders no filter whose options are
 * missing. The dropdown would simply not appear, which is the same silent
 * failure `toListingQuery` avoids on the way out.
 *
 * The known five are defaulted on top, since the V1 listing indexes them
 * directly and a configured filter is gated on `Array.isArray`. The two
 * camelCase aliases are what the V1 listing already reads.
 */
export const getSubmittalFilters = async (dataElementId = undefined) => {
  try {
    const response = await apiClient.get(`${BASE}/filters`, {
      params: dataElementId ? { data_element_id: dataElementId } : undefined,
    })

    const data = response.data || {}
    return {
      ...data,
      data_element_ids: data.data_element_ids || [],
      project_contracts: data.project_contracts || [],
      main_contractors: data.main_contractors || [],
      submission_months: data.submission_months || [],
      statuses: data.statuses || [],
      dataElementIds: data.data_element_ids || [],
      project_contract_names: (data.project_contracts || []).map(c => c.project_contract_name),
      success: true,
    }
  } catch (error) {
    console.error('Error fetching submittal filters:', error)
    throw new Error(error.response?.data?.detail || 'Failed to fetch filters')
  }
}
